import { Link, usePage } from "@inertiajs/react"
import { ArrowRight, Check, ChevronUp, Globe2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { BrandMark } from "@/components/brand-mark"
import type { MarketingLocale } from "@/hooks/use-marketing-locale"
import { marketingLocales, useMarketingLocale } from "@/hooks/use-marketing-locale"
import { productEntry } from "@/lib/product-entry"

const footerGroups = [
  {
    label: "Product",
    labelKey: "footer.product",
    links: [
      ["Case workspace", "/product#case-workspace"],
      ["Evidence timelines", "/product#evidence"],
      ["Governed actions", "/product#actions"],
      ["Pricing", "/pricing"],
    ],
  },
  {
    label: "Solutions",
    labelKey: "nav.solutions",
    links: [
      ["Support teams", "/how-it-works#support"],
      ["Customer operations", "/how-it-works#operations"],
      ["Engineering governance", "/how-it-works#engineering"],
      ["Specialist B2B SaaS", "/product#b2b-saas"],
    ],
  },
  {
    label: "Company",
    labelKey: "footer.company",
    links: [
      ["How it works", "/how-it-works"],
      ["Security", "/security"],
      ["Contact", "/contact"],
      ["Sign in", "/sign-in"],
    ],
  },
  {
    label: "Resources",
    labelKey: "footer.resources",
    links: [
      ["Product guide", "/product"],
      ["Integrations", "/integrations"],
      ["API and webhooks", "/integrations#api"],
      ["Reliability", "/security#reliability"],
    ],
  },
  {
    label: "Legal",
    labelKey: "footer.legal",
    links: [
      ["Privacy", "/legal/privacy"],
      ["Privacy controls", "/legal/privacy"],
      ["Terms", "/legal/terms"],
      ["Accessibility", "/security#accessibility"],
    ],
  },
] as const

const routesWithPageCta = new Set([
  "/",
  "/customer-stories",
  "/industries",
  "/integrations",
  "/pricing",
  "/product",
  "/resources",
  "/security",
  "/solutions",
])

export function MarketingFooter() {
  const { locale, updateLocale, t } = useMarketingLocale()
  const { url } = usePage()
  const languageRef = useRef<HTMLDivElement>(null)
  const languageTriggerRef = useRef<HTMLButtonElement>(null)
  const [languageOpen, setLanguageOpen] = useState(false)
  const showLead = !routesWithPageCta.has(url.split(/[?#]/)[0] ?? "/")
  const currentLanguage =
    marketingLocales.find(({ code }) => code === locale) ?? marketingLocales[0]
  const focusLanguageOption = (position: "current" | "first" | "last") => {
    window.requestAnimationFrame(() => {
      const options = Array.from(
        languageRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') || [],
      )
      const option =
        position === "last"
          ? options.at(-1)
          : position === "first"
            ? options[0]
            : options.find((item) => item.getAttribute("aria-selected") === "true") || options[0]

      option?.focus()
    })
  }

  useEffect(() => {
    if (!languageOpen) {
      return
    }

    const closeOutside = (event: PointerEvent) => {
      if (!languageRef.current?.contains(event.target as Node)) {
        setLanguageOpen(false)
      }
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLanguageOpen(false)
        languageTriggerRef.current?.focus()
      }
    }

    document.addEventListener("pointerdown", closeOutside)
    document.addEventListener("keydown", closeOnEscape)

    return () => {
      document.removeEventListener("pointerdown", closeOutside)
      document.removeEventListener("keydown", closeOnEscape)
    }
  }, [languageOpen])

  return (
    <footer className="marketing-footer" data-assistant-tone="dark">
      <div className="marketing-shell">
        {showLead && (
          <div className="marketing-footer__lead">
            <p>{t("footer.statement")}</p>
            <div>
              <Link href="/contact">
                {t("cta.secondary")}
                <ArrowRight aria-hidden="true" />
              </Link>
              <Link href={productEntry}>{t("nav.createAccount")}</Link>
            </div>
          </div>
        )}
        <div className="marketing-footer__index">
          <div className="marketing-footer__brand">
            <div className="marketing-footer__brand-head">
              <Link href="/" className="marketing-wordmark">
                <span className="marketing-wordmark__mark">
                  <BrandMark />
                </span>
                <span>Resolto</span>
              </Link>
              <div ref={languageRef} className="marketing-footer-language">
                <button
                  ref={languageTriggerRef}
                  type="button"
                  className="marketing-footer-language__trigger"
                  aria-label={t("nav.language")}
                  aria-haspopup="listbox"
                  aria-expanded={languageOpen}
                  aria-controls="footer-language-options"
                  onClick={() => {
                    setLanguageOpen((open) => !open)

                    if (!languageOpen) {
                      focusLanguageOption("current")
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                      event.preventDefault()
                      setLanguageOpen(true)
                      focusLanguageOption(event.key === "ArrowUp" ? "last" : "first")
                    }
                  }}
                >
                  <Globe2 aria-hidden="true" />
                  <span>{currentLanguage?.label}</span>
                  <ChevronUp aria-hidden="true" />
                </button>
                {languageOpen && (
                  <div
                    id="footer-language-options"
                    className="marketing-footer-language__menu"
                    role="listbox"
                    aria-label={t("nav.language")}
                    onKeyDown={(event) => {
                      const options = Array.from(
                        event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="option"]'),
                      )
                      const active = options.indexOf(document.activeElement as HTMLButtonElement)
                      const targets: Record<string, number> = {
                        ArrowDown: (active + 1) % options.length,
                        ArrowUp: (active - 1 + options.length) % options.length,
                        Home: 0,
                        End: options.length - 1,
                      }

                      if (event.key in targets) {
                        event.preventDefault()
                        const nextIndex = targets[event.key]
                        if (nextIndex !== undefined) {
                          options[nextIndex]?.focus()
                        }
                      }
                    }}
                  >
                    {marketingLocales.map(({ code, label, short }) => (
                      <button
                        key={code}
                        type="button"
                        role="option"
                        aria-selected={locale === code}
                        onClick={() => {
                          updateLocale(code as MarketingLocale)
                          setLanguageOpen(false)
                          languageTriggerRef.current?.focus()
                        }}
                      >
                        <span>{short}</span>
                        <strong>{label}</strong>
                        {locale === code && <Check aria-hidden="true" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p>
              Evidence-backed internal support with accountable ownership and developer-governed
              action.
            </p>
          </div>
          {footerGroups.map((group) => (
            <nav key={group.label} aria-label={`${group.label} footer links`}>
              <strong>{t(group.labelKey)}</strong>
              {group.links.map(([label, href]) => (
                <Link key={`${label}-${href}`} href={href}>
                  {label}
                </Link>
              ))}
            </nav>
          ))}
        </div>
        <div className="marketing-footer__meta">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("resolto:open-cookie-settings"))}
          >
            {t("footer.cookies")}
          </button>
          <p>
            © {new Date().getFullYear()} Resolto. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  )
}
